/**
 * Banker-Recon LO Profitability — pure routing derivation + the route-aware save
 * strip helper. Extracted from BankerRecon.tsx so they are unit-testable without
 * the component's mock surface. No React/state/I-O — keep it that way.
 */

import {
  ROUTABLE_CREDIT_TYPES,
  isAllowedProfitabilityRoutingTarget,
  type ProfitabilityRouting,
  type ProfitabilityRoutingTarget,
  type RoutableCreditType
} from '@/utils/banker-recon/loProfitability';
import { type CompensationState, type RevenueState, toNumber } from './types';
import {
  LO_COMP_REDUCTION_TYPE,
  type LoCompReductionImportMetadata
} from './loCompReduction';

/** User-adjusted routing choice carried across re-derivations and save/reload. */
export type RoutingChoices = Partial<
  Record<RoutableCreditType, { target: ProfitabilityRoutingTarget; amount?: number }>
>;

/**
 * Clamp a routed amount to a valid `[0, sourceAmount]` range — guards both bounds
 * so bad/negative persisted routing or line-item data can never produce a
 * negative routed amount (upper bound alone is insufficient).
 */
export const clampAmount = (value: number, sourceAmount: number): number =>
  Math.min(Math.max(0, toNumber(value)), Math.max(0, toNumber(sourceAmount)));

/**
 * Derive the routing-aware `ProfitabilityRouting` map from current recon state.
 *
 * A matching `lo_deduction` remains the source of truth for `base_comp` because
 * it is what actually changes payout. Without one, an explicit valid non-base
 * choice is used so `company` survives reload; otherwise the adjustment defaults
 * to `lo_profitability`.
 *
 * `sourceAmount` for `lo_comp_reduction` is taken from `loCompReductionMetadata`
 * (the canonical reduction amount used by `getDeductionSourceAmount`) so the
 * credit stays representable even after its deduction is removed (profitability
 * routing), keeping the toggle reversible.
 */
export function deriveProfitabilityRouting(params: {
  revenue: RevenueState;
  compensation: CompensationState;
  loCompReductionMetadata: LoCompReductionImportMetadata | null;
  routingChoices: RoutingChoices;
}): ProfitabilityRouting {
  const { revenue, compensation, loCompReductionMetadata, routingChoices } = params;
  const deductions = compensation.deductions ?? [];

  const sourceAmountFor = (type: RoutableCreditType): number => {
    switch (type) {
      case 'lender_credit':
        return toNumber(revenue.lenderCredit);
      case 'cure_applied_lender_credit':
        return toNumber(revenue.cureAppliedLenderCredit);
      case 'lo_comp_reduction':
        return toNumber(loCompReductionMetadata?.reductionAmount ?? 0);
    }
  };

  const routing: ProfitabilityRouting = {};
  for (const type of ROUTABLE_CREDIT_TYPES) {
    const sourceAmount = sourceAmountFor(type);
    if (sourceAmount <= 0) {
      continue;
    }
    const baseCompDeduction = deductions.find(d => d.type === type);
    if (baseCompDeduction) {
      routing[type] = {
        target: 'base_comp',
        amount: clampAmount(toNumber(baseCompDeduction.deductionAmount), sourceAmount),
        sourceAmount
      };
    } else {
      const choice = routingChoices[type];
      const target = choice
        && choice.target !== 'base_comp'
        && isAllowedProfitabilityRoutingTarget(type, choice.target)
        ? choice.target
        : 'lo_profitability';
      routing[type] = {
        target,
        amount: clampAmount(toNumber(choice?.amount ?? sourceAmount), sourceAmount),
        sourceAmount
      };
    }
  }
  return routing;
}

/**
 * FIX-1 strip helper (pure): `normalizeLoCompReductionForRecon` re-adds the LOCR
 * base-comp deduction; when the user routed LOCR to Profitability, strip it so
 * the saved line items + derived routing honor the profitability route (no
 * payout cut). No-op when LOCR is not profitability-routed.
 */
export function stripProfitabilityRoutedLoCompReduction(
  compensation: CompensationState,
  routing: RoutingChoices
): CompensationState {
  if (routing[LO_COMP_REDUCTION_TYPE]?.target !== 'lo_profitability') {
    return compensation;
  }
  return {
    ...compensation,
    deductions: (compensation.deductions ?? []).filter(d => d.type !== LO_COMP_REDUCTION_TYPE)
  };
}
